import React, { useState } from "react";
import { Check, Boxes, Sparkles, LineChart, Trophy, Truck, FileText } from "lucide-react";
import { useReveal } from "./landingUtils";

export function DineroInvisible() {
  const [ref, cls] = useReveal();
  return (
    <section className="py-24 sm:py-32 bg-[#0b1f3f] text-white relative overflow-hidden" data-testid="landing-dinero">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
      <div ref={ref} className={"relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center " + cls}>
        <div className="relative">
          <BrowserFrame src="/img/app-stock-d.webp" alt="Stock D sin movimiento" testid="dinero-frame" />
          <div className="absolute -bottom-6 -right-4 sm:right-8 bg-white text-slate-900 rounded-2xl shadow-2xl px-5 py-4 float-y z-10">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">D real detectado</div>
            <div className="font-display text-2xl font-extrabold text-red-600">Producto a producto</div>
          </div>
        </div>
        <div>
          <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#5aa2f0]">El dinero invisible</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3 leading-tight">Cada caja que no sale de la balda es dinero que no ves.</h2>
          <p className="text-blue-100/80 mt-6 text-base md:text-lg leading-relaxed">No es comprar peor: es comprar y vender con datos. Farmanaliza te enseña qué tienes parado, desde cuándo, de qué laboratorio, y qué sustitutivo se vende en su lugar.</p>
          <ul className="mt-8 space-y-4">
            {["Stock D real: sin ventas en el año y con entrada de hace más de 1 año", "Ventas y stock comparados siempre a PVP, sin trampas", "Sustitutivos de parafarmacia agrupados por IA, con margen medio", "Ranking de laboratorios con más stock parado para negociar devoluciones"].map((t) => (
              <li key={t} className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-400/15 flex items-center justify-center shrink-0"><Check size={14} className="text-emerald-400" /></span><span className="text-blue-50">{t}</span></li>
            ))}
          </ul>
          <a href="#contacto" className="inline-flex mt-10 px-6 py-3.5 rounded-full bg-white text-[#12305e] font-bold hover:bg-blue-50 transition-colors" data-testid="dinero-cta">Quiero verlo con mis datos</a>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Boxes, t: "Stock ABCD y D real", d: "Siete niveles de rotación, stock parado con fecha de entrada y evolución del inventario estudio a estudio.", img: "/img/farmanaliza-cuadro.jpg?v=2", big: true },
  { icon: Sparkles, t: "Sustitutivos con IA", d: "Productos que se sustituyen entre sí, con stock, ventas, laboratorios y margen medio.", img: "/img/farmanaliza-inventario.jpg?v=2" },
  { icon: LineChart, t: "Ventas mes a mes", d: "Comparativa con el mes anterior y el mismo mes del año pasado, consejo y productos estrella.", img: "/img/farmanaliza-ventas.jpg?v=2" },
  { icon: Trophy, t: "Equipo e incentivos", d: "Ranking, fichas individuales, incentivo de grupo y de stock D real.", img: "/img/farmanaliza-incentivos.jpg?v=2" },
  { icon: Truck, t: "Laboratorios y pedidos", d: "Ventas, stock y eficiencia por proveedor, mínimos y máximos por venta real.", img: "/img/farmanaliza-proveedores.jpg?v=2" },
  { icon: FileText, t: "Informes PDF por email", d: "Cada persona del equipo recibe su informe con su lista de productos a recomendar.", img: "/img/farmanaliza-informes.jpg?v=2", full: true },
];

export function Incluye() {
  const [ref, cls] = useReveal();
  return (
    <section id="incluye" className="py-24 sm:py-32 bg-white" data-testid="landing-incluye">
      <div ref={ref} className={"max-w-7xl mx-auto px-5 sm:px-8 " + cls}>
        <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#1f6fd1]">Qué incluye</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#12305e] mt-3 max-w-3xl">Todo lo que necesitas para que tu stock trabaje para ti.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
          {FEATURES.map(({ icon: I, t, d, img, big, full }, i) => (
            <article key={t} className={"group relative rounded-3xl overflow-hidden bg-[#0b1f3f] min-h-[360px] flex flex-col justify-end " + (big ? "lg:col-span-2" : full ? "sm:col-span-2 lg:col-span-3" : "")} data-testid={`feature-${i}`}>
              <img src={img} alt={t} className="absolute inset-0 w-full h-full object-cover origin-[80%_70%] scale-[1.35] opacity-70 group-hover:opacity-90 group-hover:scale-[1.42] transition-[transform,opacity] duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f3f] via-[#0b1f3f]/70 to-transparent" />
              <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#0b1f3f] to-transparent" />
              <div className="relative p-7">
                <span className="h-11 w-11 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white flex items-center justify-center"><I size={20} /></span>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-white mt-5">{t}</h3>
                <p className="text-blue-100/85 mt-2 text-sm leading-relaxed max-w-md">{d}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const SHOW = [
  ["Farmacia · Parafarmacia", "/img/app-segmentacion.webp", "Stock, ventas, rotación y D muerto separados por canal, con los laboratorios más y menos eficientes de cada uno."],
  ["Stock D sin movimiento", "/img/app-stock-d.webp", "Todo el stock sin ventas, separado en D muerto y D reciente, y ordenado por el año de su última entrada. Pulsa un año para verlo por familias."],
  ["Cuadro de ventas", "/img/app-cuadro-ventas.webp", "Facturación, ticket medio, venta libre, parafarmacia y consejo frente al mes anterior, al año pasado y a la media de 12 meses."],
  ["Ficha del empleado", "/img/app-ficha.webp?v=2", "Los indicadores de cada persona frente a la media de la farmacia y a su propio histórico, con su evolución mes a mes."],
  ["Ranking del equipo", "/img/app-ranking.webp?v=2", "Quién lidera en facturación, ticket de venta libre, ventas mixtas, consejo y parafarmacia."],
  ["Clasificación ABCD", "/img/app-abcd.webp", "Siete clases de rotación: qué % de productos tiene cada una frente al % de ventas que aporta."],
];

function BrowserFrame({ src, alt, testid }) {
  return (
    <div className="rounded-2xl overflow-hidden bg-white shadow-2xl shadow-[#12305e]/25 ring-1 ring-slate-200" data-testid={testid}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[11px] font-mono text-slate-400 truncate">farmanaliza · {alt}</span>
      </div>
      <img key={src} src={src} alt={alt} className="w-full block fade-in" loading="lazy" />
    </div>
  );
}

export function PantallasCollage() {
  const [ref, cls] = useReveal();
  return (
    <section className="relative bg-[#0b1f3f] pb-24 sm:pb-32 overflow-hidden" data-testid="landing-collage">
      <div ref={ref} className={"relative max-w-7xl mx-auto px-5 sm:px-8 " + cls}>
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#5aa2f0]">Pantallas reales</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-3">Así se ve tu farmacia en Farmanaliza.</h2>
        </div>
        <div className="relative mt-16 h-[300px] sm:h-[460px] lg:h-[560px]">
          <div className="absolute left-0 top-10 w-[62%] -rotate-3 hover:rotate-0 hover:z-30 transition-transform duration-500 z-10"><BrowserFrame src="/img/app-segmentacion.webp" alt="Farmacia y parafarmacia" testid="collage-1" /></div>
          <div className="absolute right-0 top-0 w-[52%] rotate-2 hover:rotate-0 hover:z-30 transition-transform duration-500 z-20"><BrowserFrame src="/img/app-cuadro-ventas.webp" alt="Cuadro de ventas" testid="collage-2" /></div>
          <div className="absolute left-[22%] bottom-0 w-[56%] rotate-1 hover:rotate-0 hover:z-30 transition-transform duration-500 z-20 hidden sm:block"><BrowserFrame src="/img/app-ranking.webp?v=2" alt="Ranking del equipo" testid="collage-3" /></div>
        </div>
      </div>
    </section>
  );
}

export function Showcase() {
  const [i, setI] = useState(0);
  const [ref, cls] = useReveal();
  return (
    <section className="py-24 sm:py-32 bg-[#f5f8fc]" data-testid="landing-showcase">
      <div ref={ref} className={"max-w-7xl mx-auto px-5 sm:px-8 " + cls}>
        <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#1f6fd1]">Por dentro</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#12305e] mt-3">Míralo antes de decidir.</h2>
        <div className="flex flex-wrap gap-2 mt-10" role="tablist">
          {SHOW.map(([l], k) => (
            <button key={l} onClick={() => setI(k)} role="tab" aria-selected={i === k} data-testid={`showcase-tab-${k}`}
                    className={"px-4 py-2 rounded-full text-sm font-semibold border transition-colors " + (i === k ? "bg-[#12305e] text-white border-[#12305e]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}>{l}</button>
          ))}
        </div>
        <div className="grid lg:grid-cols-[1.8fr_1fr] gap-10 mt-8 items-center">
          <div data-testid="showcase-img"><BrowserFrame src={SHOW[i][1]} alt={SHOW[i][0]} testid="showcase-frame" /></div>
          <div key={i} className="fade-in">
            <div className="font-display text-5xl font-extrabold text-[#1f6fd1]/20">0{i + 1}</div>
            <h3 className="font-display text-2xl font-bold text-slate-900 mt-2">{SHOW[i][0]}</h3>
            <p className="text-slate-600 mt-3 text-base leading-relaxed">{SHOW[i][2]}</p>
            <button onClick={() => setI((i + 1) % SHOW.length)} className="mt-6 text-sm font-bold text-[#1f6fd1] hover:underline" data-testid="showcase-next">Siguiente pantalla →</button>
          </div>
        </div>
      </div>
    </section>
  );
}
